import {
  BrandKitField,
  BrandKitFieldType,
  BrandKitListItemType,
  BrandKitVersionDto,
} from "@arrowsup/platform-dtos";
import { Box, Link, Stack, Tooltip, Typography } from "@mui/material";
import { BrandAssetFileLink, BrandAssetThumbnail } from "./brand-assets";

export const fieldTypeLabels: Record<BrandKitFieldType, string> = {
  [BrandKitFieldType.Text]: "Text",
  [BrandKitFieldType.LongText]: "Long text",
  [BrandKitFieldType.Color]: "Color",
  [BrandKitFieldType.Url]: "URL",
  [BrandKitFieldType.List]: "List",
  [BrandKitFieldType.Font]: "Font",
  [BrandKitFieldType.Image]: "Image",
};

export const listItemTypeLabels: Record<BrandKitListItemType, string> = {
  [BrandKitListItemType.Text]: "Text",
  [BrandKitListItemType.Color]: "Color",
  [BrandKitListItemType.Url]: "URL",
  [BrandKitListItemType.Image]: "Image",
};

/** "version 3", or "the draft" for an unpublished version. */
export const versionName = (version: BrandKitVersionDto): string =>
  version.versionNumber === undefined
    ? "the draft"
    : `version ${version.versionNumber}`;

export const describeFieldType = (field: BrandKitField): string =>
  field.type === BrandKitFieldType.List
    ? `List of ${listItemTypeLabels[field.itemType].toLowerCase()}`
    : fieldTypeLabels[field.type];

export const ColorSwatch: React.FC<{ color: string; size?: number }> = ({
  color,
  size = 24,
}) => (
  <Tooltip title={color}>
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        backgroundColor: color,
        verticalAlign: "middle",
      }}
    />
  </Tooltip>
);

const Empty = () => (
  <Typography variant="body2" color="text.disabled">
    Not set
  </Typography>
);

const ColorValue: React.FC<{ color: string }> = ({ color }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <ColorSwatch color={color} />
    <Typography variant="body2" fontFamily="monospace">
      {color}
    </Typography>
  </Stack>
);

const UrlValue: React.FC<{ url: string }> = ({ url }) => (
  <Link href={url} target="_blank" rel="noopener noreferrer">
    {url}
  </Link>
);

const ListItem: React.FC<{ itemType: BrandKitListItemType; item: string }> = ({
  itemType,
  item,
}) => {
  switch (itemType) {
    case BrandKitListItemType.Color:
      return <ColorValue color={item} />;
    case BrandKitListItemType.Url:
      return <UrlValue url={item} />;
    case BrandKitListItemType.Text:
      return <Typography variant="body2">{item}</Typography>;
    case BrandKitListItemType.Image:
      return <BrandAssetThumbnail id={item} />;
    // no default
  }
};

/** Swatches and images sit side by side; other items are bulleted. */
const showsInRow = (itemType: BrandKitListItemType) =>
  itemType === BrandKitListItemType.Color ||
  itemType === BrandKitListItemType.Image;

/** Read-only rendering of a field's value. */
export const BrandKitFieldValue: React.FC<{ field: BrandKitField }> = ({
  field,
}) => {
  switch (field.type) {
    case BrandKitFieldType.Text:
    case BrandKitFieldType.LongText:
      return field.value ? (
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {field.value}
        </Typography>
      ) : (
        <Empty />
      );
    case BrandKitFieldType.Color:
      return field.value ? <ColorValue color={field.value} /> : <Empty />;
    case BrandKitFieldType.Url:
      return field.value ? <UrlValue url={field.value} /> : <Empty />;
    case BrandKitFieldType.Font:
      return field.value ? (
        <Stack spacing={0.5}>
          <Typography variant="body2">
            {field.value.family}
            {field.value.fallback ? `, ${field.value.fallback}` : ""}
          </Typography>
          {field.value.cssUrl && <UrlValue url={field.value.cssUrl} />}
          {field.value.assetId && (
            <BrandAssetFileLink id={field.value.assetId} />
          )}
        </Stack>
      ) : (
        <Empty />
      );
    case BrandKitFieldType.Image:
      return field.value ? <BrandAssetThumbnail id={field.value} /> : <Empty />;
    case BrandKitFieldType.List:
      if (field.value.length === 0) {
        return <Empty />;
      }
      return showsInRow(field.itemType) ? (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {field.value.map((item, i) => (
            <ListItem key={i} itemType={field.itemType} item={item} />
          ))}
        </Stack>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          {field.value.map((item, i) => (
            <li key={i}>
              <ListItem itemType={field.itemType} item={item} />
            </li>
          ))}
        </Box>
      );
    // no default
  }
};
